import React, { useMemo, useState } from 'react';

const getLineChipStyle = (color) => {
  const hex = String(color || '').trim();
  if (!/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return { backgroundColor: '#e2e8f0', color: '#334155' };
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return {
    backgroundColor: hex,
    color: luminance > 0.6 ? '#111827' : '#ffffff',
  };
};

const splitSchools = (schools = []) => {
  const elementary = [];
  const middle = [];

  schools.forEach((item) => {
    const name = String(item?.schoolName || '').trim();
    const schoolType = String(item?.schoolType || item?.schoolLevel || '').trim();
    if (name.includes('중') || schoolType.includes('중')) {
      middle.push(item);
      return;
    }
    elementary.push(item);
  });

  return { elementary, middle };
};

const STRUCTURE_TYPE_MAP = {
  계: '계단식',
  복: '복도식',
  타: '타워형',
  혼: '혼합식',
};

const formatHouseStructure = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';

  const compact = raw.replace(/\s+/g, '');
  const m = compact.match(/^([가-힣A-Za-z]+)(\d)(\d)$/);
  if (!m) return raw;

  const typeCode = m[1];
  const roomCnt = Number(m[2]);
  const bathCnt = Number(m[3]);
  const typeText = STRUCTURE_TYPE_MAP[typeCode] || typeCode;

  const parts = [typeText];
  if (Number.isFinite(roomCnt) && roomCnt > 0) parts.push(`방${roomCnt}`);
  if (Number.isFinite(bathCnt) && bathCnt > 0) parts.push(`화장실${bathCnt}`);
  return parts.join(' ');
};

function RankingDetailComplexInfoTable({
  detail,
  summary,
  areaTypeText,
  areaTypeList,
  selectedAreaFamilyCnt,
  selectedAreaManagementCost,
  selectedAreaHoldingTax,
  formatUseApproveDate,
  formatNumber,
  formatValue,
  formatHeat,
  mainAsCards = false,
}) {
  const [areaTypeExpanded, setAreaTypeExpanded] = useState(false);

  // 각 타입 { label, active } — active=false 는 선택 평형에 속하지 않아 연하게 표시
  const areaTypeItems = useMemo(() => {
    if (Array.isArray(areaTypeList) && areaTypeList.length > 0) {
      return areaTypeList.filter((it) => it && it.label);
    }
    return String(areaTypeText || '')
      .split(',')
      .map((v) => ({ label: v.trim(), active: true }))
      .filter((it) => it.label);
  }, [areaTypeList, areaTypeText]);
  const inlineAreaTypeItems = areaTypeItems.slice(0, 4);
  const hasMoreAreaTypes = areaTypeItems.length > inlineAreaTypeItems.length;

  const renderAreaTypeSpans = (items) => (
    items.length > 0
      ? items.map((item, i) => (
        <span key={`${item.label}-${i}`} className={item.active ? '' : 'ranking-detail-area-type-dim'}>
          {item.label}{i < items.length - 1 ? ', ' : ''}
        </span>
      ))
      : '-'
  );

  const areaTypeRow = {
    label: '평형/타입',
    value: (
      <div className="ranking-detail-area-type-cell">
        <span className="ranking-detail-area-type-inline">{renderAreaTypeSpans(inlineAreaTypeItems)}</span>
        {hasMoreAreaTypes && (
          <button
            type="button"
            className={`ranking-detail-area-type-more ${areaTypeExpanded ? 'open' : ''}`}
            onClick={() => setAreaTypeExpanded((prev) => !prev)}
            aria-label="평형/타입 더보기"
            title="평형/타입 더보기"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              {areaTypeExpanded ? (
                <path d="M5 12 L10 7 L15 12" />
              ) : (
                <path d="M5 8 L10 13 L15 8" />
              )}
            </svg>
          </button>
        )}
        {areaTypeExpanded && hasMoreAreaTypes && (
          <div className="ranking-detail-area-type-popover">{renderAreaTypeSpans(areaTypeItems)}</div>
        )}
      </div>
    ),
  };

  const infoRows = [
    [
      { label: '평세대수', value: formatValue(selectedAreaFamilyCnt ?? summary?.ptpFamilyCnt, '세대') },
      { label: '구조', value: formatHouseStructure(summary?.houseStructure) },
    ],
    [
      {
        label: '관리비',
        value: selectedAreaManagementCost !== null && selectedAreaManagementCost !== undefined
          ? `${Number(selectedAreaManagementCost).toLocaleString('ko-KR')}원`
          : formatValue(summary?.maintCost ? `${summary.maintCost}만원` : '-'),
      },
      {
        label: '보유세',
        value: selectedAreaHoldingTax !== null && selectedAreaHoldingTax !== undefined
          ? `${Number(selectedAreaHoldingTax).toLocaleString('ko-KR')}원`
          : formatValue(summary?.holdingTax ? `${summary.holdingTax}만원` : '-'),
      },
    ],
    [
      { label: '동', value: formatNumber(detail?.totBldCnt, '개') },
      { label: '최고/최저층', value: `${formatValue(detail?.maxFloor)} / ${formatValue(detail?.minFloor)}` },
    ],
    [
      { label: '건설사', value: formatValue(detail?.cscoName) },
      { label: '용적/건폐율', value: `${formatValue(detail?.floorAreaRate, '%')} / ${formatValue(detail?.buildingClosingRate, '%')}` },
    ],
    [
      { label: '주차대수', value: `${formatNumber(detail?.totprkCnt, '대')}(세대당 ${formatValue(detail?.totprkCntPer)})` },
      { label: '난방', value: `${formatHeat(detail?.heatType, 'system')} / ${formatHeat(detail?.heatFul, 'energy')}` },
    ],
  ];

  const subways = Array.isArray(detail?.subways) ? detail.subways : [];
  const schools = Array.isArray(detail?.schools) ? detail.schools : [];
  const developmentRails = Array.isArray(detail?.developmentRails) ? detail.developmentRails : [];
  const developmentJigus = Array.isArray(detail?.developmentJigus) ? detail.developmentJigus : [];
  const buses = Array.isArray(detail?.buses) ? detail.buses : [];

  const groupedSubways = useMemo(() => {
    const map = new Map();

    subways.forEach((item) => {
      const stationName = String(item?.stationName || '-').trim() || '-';
      if (!map.has(stationName)) {
        map.set(stationName, {
          stationName,
          lines: [],
          walkingDistance: item?.walkingDistance ?? null,
          walkingDuration: item?.walkingDuration ?? null,
        });
      }

      const row = map.get(stationName);
      const lineName = String(item?.lineName || '').trim();
      const lineColor = String(item?.lineColor || '').trim();
      if (lineName && !row.lines.some((line) => line.lineName === lineName)) {
        row.lines.push({ lineName, lineColor });
      }

      const currentDistance = row.walkingDistance;
      const nextDistance = item?.walkingDistance;
      if (
        nextDistance !== null
        && nextDistance !== undefined
        && (currentDistance === null || currentDistance === undefined || nextDistance < currentDistance)
      ) {
        row.walkingDistance = nextDistance;
        row.walkingDuration = item?.walkingDuration ?? row.walkingDuration;
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const ad = a.walkingDistance ?? Number.POSITIVE_INFINITY;
      const bd = b.walkingDistance ?? Number.POSITIVE_INFINITY;
      return ad - bd;
    });
  }, [subways]);

  const schoolGroups = useMemo(() => splitSchools(schools), [schools]);

  return (
    <div className="ranking-detail-info-card ranking-detail-complex-info-table">
      <h3>단지 정보</h3>

      <div className="ranking-detail-complex-grid-layout">
        <section className="ranking-detail-complex-panel ranking-detail-complex-panel-main">
          <h4>단지정보</h4>
        {mainAsCards ? (
          <div className="ranking-detail-info-kv-grid">
            {[areaTypeRow, ...infoRows.flat()].map((item, index) => (
              <div className="ranking-detail-info-kv" key={`complex-info-kv-${index}`}>
                <span className="kv-label">{item.label}</span>
                <span className="kv-value">{item.value}</span>
              </div>
            ))}
          </div>
        ) : (
        <table className="ranking-detail-complex-info-grid">
          <tbody>
            <tr>
              <th>{areaTypeRow.label}</th>
              <td colSpan={3}>{areaTypeRow.value}</td>
            </tr>
            {infoRows.map((row, rowIndex) => (
              <tr key={`complex-info-row-${rowIndex}`}>
                {row.map((item, itemIndex) => (
                    <React.Fragment key={`complex-info-cell-${rowIndex}-${itemIndex}`}>
                      <th>{item.label}</th>
                      <td>{item.value}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </section>

        {mainAsCards ? (
          <section className="ranking-detail-complex-panel ranking-detail-complex-panel-school ranking-detail-complex-panel-school-merged">
            <h4>학군</h4>
            <div className="ranking-detail-school-split">
              <div className="ranking-detail-school-col">
                <div className="ranking-detail-school-col-title">초등학교({schoolGroups.elementary.length})</div>
                <ul className="ranking-detail-compact-list">
                  {schoolGroups.elementary.map((item, idx) => (
                    <li key={`el-${idx}`}>
                      <div className="compact-list-top"><span className="compact-list-name">{item.schoolName || '-'}</span></div>
                      <div className="compact-list-meta">
                        {item.distance ? `${Number(item.distance).toLocaleString('ko-KR')}m` : '-'}
                        {item.walkingMinute ? ` · 도보 ${item.walkingMinute}분` : ''}
                      </div>
                    </li>
                  ))}
                  {schoolGroups.elementary.length === 0 && <li className="compact-list-meta">-</li>}
                </ul>
              </div>
              <div className="ranking-detail-school-col">
                <div className="ranking-detail-school-col-title">중학교({schoolGroups.middle.length})</div>
                <ul className="ranking-detail-compact-list">
                  {schoolGroups.middle.map((item, idx) => (
                    <li key={`mid-${idx}`}>
                      <div className="compact-list-top"><span className="compact-list-name">{item.schoolName || '-'}</span></div>
                      <div className="compact-list-meta">
                        {item.distance ? `${Number(item.distance).toLocaleString('ko-KR')}m` : '-'}
                        {item.walkingMinute ? ` · 도보 ${item.walkingMinute}분` : ''}
                      </div>
                    </li>
                  ))}
                  {schoolGroups.middle.length === 0 && <li className="compact-list-meta">-</li>}
                </ul>
              </div>
            </div>
          </section>
        ) : (
          <section className="ranking-detail-complex-panel ranking-detail-complex-panel-school">
            <h4>중학교</h4>
            <div className="ranking-detail-school-scroll">
              <ul className="ranking-detail-compact-list">
                {schoolGroups.middle.map((item, idx) => (
                  <li key={`middle-school-${idx}`}>
                    <div className="compact-list-top">
                      <span className="compact-list-name">{item.schoolName || '-'}</span>
                    </div>
                    <div className="compact-list-meta">
                      {item.distance ? `${Number(item.distance).toLocaleString('ko-KR')}m` : '-'}
                      {item.walkingMinute ? ` · 도보 ${item.walkingMinute}분` : ''}
                    </div>
                  </li>
                ))}
                {schoolGroups.middle.length === 0 && <li className="compact-list-meta">-</li>}
              </ul>
            </div>
          </section>
        )}

        {mainAsCards ? (
          <section className="ranking-detail-complex-panel ranking-detail-complex-panel-subway ranking-detail-complex-panel-transit">
            <h4>교통</h4>
            <div className="ranking-detail-school-split">
              <div className="ranking-detail-school-col">
                <div className="ranking-detail-school-col-title">지하철</div>
                <ul className="ranking-detail-compact-list">
                  {groupedSubways.map((item, idx) => (
                    <li key={`subway-${idx}`}>
                      <div className="compact-list-top">
                        <span className="compact-list-name">{item.stationName}</span>
                        <span className="subway-line-badges">
                          {item.lines.map((line, lineIdx) => (
                            <span key={`subway-line-${idx}-${lineIdx}`} className="subway-line-chip" style={getLineChipStyle(line.lineColor)}>
                              {line.lineName}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className="compact-list-meta">
                        {item.walkingDistance != null ? `${Number(item.walkingDistance).toLocaleString('ko-KR')}m` : '-'}
                        {item.walkingDuration != null ? ` · 도보 ${item.walkingDuration}분` : ''}
                      </div>
                    </li>
                  ))}
                  {groupedSubways.length === 0 && <li className="compact-list-meta">-</li>}
                </ul>
              </div>
              <div className="ranking-detail-school-col">
                <div className="ranking-detail-school-col-title">버스노선</div>
                <ul className="ranking-detail-compact-list">
                  {buses.slice(0, 6).map((item, idx) => (
                    <li key={`bus-${idx}`}>
                      <div className="compact-list-top">
                        <span className="compact-list-name">{String(item?.routeName || item?.name || '-')}</span>
                      </div>
                    </li>
                  ))}
                  {buses.length === 0 && <li className="compact-list-meta">정보 없음</li>}
                </ul>
              </div>
            </div>
          </section>
        ) : (
          <section className="ranking-detail-complex-panel ranking-detail-complex-panel-subway">
            <h4>지하철</h4>
            <div className="ranking-detail-subway-scroll">
              <ul className="ranking-detail-compact-list">
                {groupedSubways.map((item, idx) => (
                <li key={`subway-${idx}`}>
                  <div className="compact-list-top">
                    <span className="compact-list-name">{item.stationName}</span>
                    <span className="subway-line-badges">
                      {item.lines.map((line, lineIdx) => (
                        <span
                          key={`subway-line-${idx}-${lineIdx}`}
                          className="subway-line-chip"
                          style={getLineChipStyle(line.lineColor)}
                        >
                          {line.lineName}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="compact-list-meta">
                    {item.walkingDistance != null ? `${Number(item.walkingDistance).toLocaleString('ko-KR')}m` : '-'}
                    {item.walkingDuration != null ? ` · 도보 ${item.walkingDuration}분` : ''}
                  </div>
                </li>
                ))}
                {groupedSubways.length === 0 && <li className="compact-list-meta">-</li>}
              </ul>
            </div>
          </section>
        )}

        <section className="ranking-detail-complex-panel ranking-detail-complex-panel-development">
          <h4>개발구역</h4>
          <ul className="ranking-detail-compact-list">
            {developmentRails.slice(0, 3).map((item, idx) => (
              <li key={`dev-rail-${idx}`}>
                <div className="compact-list-top">
                  <span className="compact-list-name">{item.stationName || '-'}</span>
                  {item.openDate && <span className="compact-list-badge">{item.openDate}</span>}
                </div>
                <div className="compact-list-meta">{item.railName || '-'}</div>
              </li>
            ))}
            {developmentJigus.slice(0, 3).map((item, idx) => (
              <li key={`dev-jigu-${idx}`}>
                <div className="compact-list-top">
                  <span className="compact-list-name">{item.name || '-'}</span>
                  {item.step && <span className="compact-list-badge">{item.step}</span>}
                </div>
                <div className="compact-list-meta">{item.typeName || '-'}</div>
              </li>
            ))}
            {developmentRails.length === 0 && developmentJigus.length === 0 && <li className="compact-list-meta">-</li>}
          </ul>
        </section>

        {!mainAsCards && (
        <section className="ranking-detail-complex-panel ranking-detail-complex-panel-school">
          <h4>초등학교</h4>
          <div className="ranking-detail-school-scroll">
            <ul className="ranking-detail-compact-list">
              {schoolGroups.elementary.map((item, idx) => (
                <li key={`elementary-school-${idx}`}>
                  <div className="compact-list-top">
                    <span className="compact-list-name">{item.schoolName || '-'}</span>
                  </div>
                  <div className="compact-list-meta">
                    {item.distance ? `${Number(item.distance).toLocaleString('ko-KR')}m` : '-'}
                    {item.walkingMinute ? ` · 도보 ${item.walkingMinute}분` : ''}
                  </div>
                </li>
              ))}
              {schoolGroups.elementary.length === 0 && <li className="compact-list-meta">-</li>}
            </ul>
          </div>
        </section>
        )}

        {!mainAsCards && (
        <section className="ranking-detail-complex-panel">
          <h4>버스노선</h4>
          <ul className="ranking-detail-compact-list">
            {buses.slice(0, 6).map((item, idx) => (
              <li key={`bus-${idx}`}>
                <div className="compact-list-top">
                  <span className="compact-list-name">{String(item?.routeName || item?.name || '-')}</span>
                </div>
              </li>
            ))}
            {buses.length === 0 && <li className="compact-list-meta">-</li>}
          </ul>
        </section>
        )}

      </div>
    </div>
  );
}

export default RankingDetailComplexInfoTable;
